import './new.scss';
import Sidebar from '../../components/sidebar/Sidebar';
import Navbar from '../../components/navbar/Navbar';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import { useEffect, useState } from 'react';
import { dbService } from '../../services/database-abstraction';
import { supabase } from '../../utils/supabase.js';
import { useNavigate } from 'react-router-dom';
import useStaffDetails from '../../hooks/useStaffDetails';
import useRolePermissions from '../../hooks/useRolePermissions';
import ReadOnlyForm from '../../components/ReadOnlyForm/ReadOnlyForm';

const New = ({ inputs, title }) => {
  const [file, setFile] = useState('');
  const [data, setData] = useState({});
  const [per, setPerc] = useState(null);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { staffDetails } = useStaffDetails();
  const { isReadOnlyRole } = useRolePermissions();

  useEffect(() => {
    setData((prevData) => ({ ...prevData, trust: 'individual' }));
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      setErrors((prev) => ({
        ...prev,
        file: 'Please select an image or PDF.',
      }));
      return;
    }

    // File Size Validation
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (selectedFile.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        file: 'File size should not exceed 5MB.',
      }));
      return;
    }

    // File Type Validation (you can add or remove formats as needed)
    const allowedFormats = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/pdf', // allow PDF upload
    ];
    if (!allowedFormats.includes(selectedFile.type)) {
      setErrors((prev) => ({
        ...prev,
        file: 'Please upload a valid file format (JPEG, JPG, PNG, GIF, PDF).',
      }));
      return;
    }

    // If everything's fine, proceed with setting the file
    setFile(selectedFile);
  };

  const handleInputChange = (e, inputId) => {
    handleInput(e);
  };

  useEffect(() => {
    if (!file) return;

    const uploadFile = async () => {
      const path = `users/new/${Date.now()}_${file.name}`;
      setPerc(0);

      const { error } = await supabase.storage
        .from('user-images')
        .upload(path, file, { upsert: true, contentType: file.type });

      if (error) {
        console.error('Upload error:', error);
        setErrors((prev) => ({ ...prev, file: 'Error uploading image. Please try again.' }));
        setPerc(null);
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('user-images')
        .getPublicUrl(path);

      setData((prev) => ({ ...prev, img: publicUrl }));
      setErrors((prev) => ({ ...prev, file: null }));
      setPerc(100);
      console.log('✅ Image uploaded to Supabase Storage:', publicUrl);
    };

    uploadFile();
  }, [file]);

  const validateFields = () => {
    let tempErrors = {};

    // Email validation
    if (!data.email) {
      tempErrors.email = 'Email is required.';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        tempErrors.email =
          'Please enter a valid email address (e.g., user@gmail.com).';
      }
    }

    // Phone validation
    if (!data.phone || data.phone.length !== 10) {
      tempErrors.phone = 'Please enter a valid 10-digit phone number.';
    }

    // Branch code validation
    if (!data.code || data.code === '') {
      tempErrors.code = 'Please select a branch code.';
    }

    // Display name validation
    if (!data.displayName || data.displayName.trim() === '') {
      tempErrors.displayName = 'Display name is required.';
    }

    // Add validations for corporation-specific fields
    if (data.trust === 'corporation') {
      if (!data.corpName) {
        tempErrors.corpName = 'Please enter the corporation name.';
      }
      if (!data.doi) {
        tempErrors.doi = 'Please select the date of incorporation.';
      }
      if (!data.tax) {
        tempErrors.tax = 'Please enter the tax identification number.';
      }
      if (!file) {
        tempErrors.file = 'Please upload the certificate of incorporation.';
      }
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleInput = (e) => {
    const { id, value } = e.target;

    let newErrors = {};

    // Real-time validation
    if (id === 'phone' && value.length > 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits.';
    }

    if (id === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        newErrors.email =
          'Please enter a valid email address (e.g., user@gmail.com).';
      }
    }

    setData((prevData) => ({ ...prevData, [id]: value }));

    // Clear corporation fields if 'individual' is selected
    if (id === 'trust' && value === 'individual') {
      setData((prevData) => ({
        ...prevData,
        corpName: '', // Clear Name of Corporation
        doi: '', // Clear Date of Incorporation
        tax: '', // Clear Tax Identification No
        certificate: '', // Clear Certificate if it's set
      }));
    }

    // Clear errors for fields that are now valid
    if (id === 'email' && value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      newErrors.email = '';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!validateFields()) return;

    try {
      console.log('🚀 Starting user creation process...');

      // Membership code is assigned by the BACKEND (next_membership_code):
      // it is advisory-locked and uses GREATEST(counter, MAX existing code)+1,
      // so it never collides — even under concurrent creates or a stale
      // counter. The frontend must NOT generate or send a code (doing so
      // caused KPRBxxxx duplicate errors when the counter drifted).

      // 2. Create user in PostgreSQL (ID + membership code generated server-side)
      console.log('📝 Step 2: Creating user in PostgreSQL...');
      console.log('👤 Staff details:', staffDetails);
      console.log('📋 Form data fields:', Object.keys(data));
      console.log('📋 Form data values (ALL):', data);
      console.log('📋 Key field mappings:');
      console.log(`   Form 'card' -> 'ghana_card': ${data.card}`);
      console.log(`   Form 'kin' -> 'next_of_kin': ${data.kin}`);
      console.log(
        `   Form 'kin-contact' -> 'next_of_kin_contact': ${data['kin-contact']}`
      );
      console.log(`   Form 'channel' -> 'payment_channel': ${data.channel}`);
      console.log(`   Form 'account' -> 'account_number': ${data.account}`);
      console.log(`   Form 'code' -> 'branch_code': ${data.code}`);
      console.log(`   Form 'inputter' -> 'inputter_name': ${data.inputter}`);
      console.log(`   Form 'dob' -> 'date_of_birth': ${data.dob}`);
      console.log(`   Form 'dop' -> 'date_of_purchase': ${data.dop}`);
      console.log(`   Form 'digital' -> 'digital_address': ${data.digital}`);

      const newUser = {
        // Note: id will be generated server-side using UUID
        display_name: data.displayName,
        email: data.email,
        address: data.address,
        phone: data.phone,
        share_amount: data.share || 0, // Map 'share' field to share_amount
        share_value: data.price || 0, // Map 'price' field to share_value
        status: 'pending',
        // membership_code intentionally omitted — the backend assigns it
        ghana_card: data.card || null, // Map 'card' field to ghana_card
        next_of_kin: data.kin || null, // Map 'kin' field to next_of_kin
        kin_contact: data['kin-contact'] || '', // Legacy field with hyphenated key
        next_of_kin_contact: data['kin-contact'] || '', // Map 'kin-contact' to next_of_kin_contact
        account_number: data.account || null, // Map 'account' field to account_number
        payment_channel: data.channel || null, // Map 'channel' field to payment_channel
        branch_code: data.code || null, // Map 'code' field to branch_code
        img_url: data.img || '',
        bio: data.bio || '',
        inputter: data.inputter || staffDetails?.name || 'Unknown Staff', // Legacy field
        inputter_name: data.inputter || staffDetails?.name || 'Unknown Staff', // Map 'inputter' field
        added_by: data.inputter || staffDetails?.name || 'System',
        beneficiary_type: data.trust || 'individual',
        corp_name: data.corpName || null,
        doi: data.doi || null,
        tax_id: data.tax || null,
        // Map form fields to database fields
        date_of_birth: data.dob || null, // Map 'dob' field to date_of_birth
        date_of_purchase: data.dop || null, // Map 'dop' field to date_of_purchase
        digital_address: data.digital || null, // Map 'digital' field to digital_address
        // Additional fields for completeness
        dob: data.dob || null, // Legacy field (same as date_of_birth)
        gender: data.gender || null,
        other_info: data.otherInfo || null,
        previous_name: data.previousName || null,
        user_role: 'user',
      };

      console.log('📤 Sending user data to backend:');
      console.log('   Complete newUser object:', newUser);
      console.log('   Core fields:', {
        display_name: newUser.display_name,
        email: newUser.email,
        membership_code: '(assigned by backend)',
        inputter_name: newUser.inputter_name,
        next_of_kin_contact: newUser.next_of_kin_contact,
      });
      console.log('   All fields:', Object.keys(newUser));

      const createResult = await dbService.createUser(newUser);
      console.log('📥 Backend response:', createResult);

      if (createResult.error) {
        throw new Error(createResult.error);
      }

      if (!createResult) {
        throw new Error('No response from server');
      }

      console.log('✅ User created successfully:', createResult);

      // 3. Record attendance if year is selected. Uses the AUTHENTICATED
      // service method — the previous raw fetch() sent no auth token and
      // always failed with 401.
      if (data.attendance && createResult.data?.id) {
        console.log('📝 Step 3: Recording attendance for year:', data.attendance);
        try {
          await dbService.recordAttendance({
            user_id: createResult.data.id,
            year: data.attendance,
          });
          console.log('✅ Attendance recorded');
        } catch (attendanceError) {
          console.warn('⚠️ Error recording attendance:', attendanceError);
          // Don't fail the entire process if attendance recording fails
        }
      }

      // Note: Membership counter is now updated atomically in the backend
      console.log(
        '✅ User creation process completed successfully (counter updated atomically)'
      );
      navigate(-1);
    } catch (error) {
      console.error('❌ Error adding user:', error);
      console.error('   Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });

      // Provide user-friendly error messages for common issues
      let userFriendlyMessage = error.message;

      if (error.message.includes('users_email_format')) {
        userFriendlyMessage =
          'Please enter a valid email address (e.g., user@gmail.com). The email format is invalid.';
      } else if (
        error.message.includes('duplicate key value violates unique constraint')
      ) {
        if (error.message.includes('users_email_key')) {
          userFriendlyMessage =
            'This email address is already registered. Please use a different email.';
        } else if (error.message.includes('users_phone_key')) {
          userFriendlyMessage =
            'This phone number is already registered. Please use a different phone number.';
        } else {
          userFriendlyMessage =
            'This information is already registered. Please check your details.';
        }
      } else if (error.message.includes('violates check constraint')) {
        userFriendlyMessage =
          'Please check your input data. Some fields contain invalid information.';
      } else if (
        error.message.includes('Request failed with status code 500')
      ) {
        userFriendlyMessage =
          'Server error occurred. Please check your input data and try again.';
      }

      setErrors((prev) => ({
        ...prev,
        general: `An error occurred while registering: ${userFriendlyMessage}`,
      }));
    }
  };

  return (
    <div className="new">
      <Sidebar />
      <div className="newContainer">
        <Navbar />
        <div className="top">
          <h1>{title}</h1>
        </div>
        <div className="bottom">
          <div className="left">
            <img
              src={
                file
                  ? URL.createObjectURL(file)
                  : 'https://icon-library.com/images/no-image-icon/no-image-icon-0.jpg'
              }
              alt="Profile"
            />
            <div className="image-upload-hint">
              Click the upload icon to select a profile image
            </div>
          </div>
          <div className="right">
            <ReadOnlyForm onSubmit={handleAdd}>
              <div className="formInput">
                <label htmlFor="file">
                  Profile Image{' '}
                  <DriveFolderUploadOutlinedIcon className="icon" />
                </label>
                <input
                  type="file"
                  id="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept="image/*,application/pdf"
                />
                {errors.file && <div className="error">{errors.file}</div>}
                {per !== null && per < 100 && (
                  <div className="upload-progress">
                    <div
                      className="progress-bar"
                      style={{ width: `${per}%` }}
                    ></div>
                  </div>
                )}
                {per === 100 && (
                  <div className="success">Upload completed!</div>
                )}
              </div>

              {inputs.map((input) => (
                <div className="formInput" key={input.id}>
                  <label htmlFor={input.id}>{input.label}</label>
                  {input.type === 'select' ? (
                    <select
                      id={input.id}
                      onChange={(e) => handleInput(e)}
                      required={input.isRequired !== false}
                      value={data[input.id] || ''}
                    >
                      {input.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={input.id}
                      type={input.type}
                      placeholder={input.placeholder}
                      onChange={handleInput}
                      required={input.isRequired !== false}
                      value={data[input.id] || ''}
                      autoComplete="off"
                    />
                  )}
                  {errors[input.id] && (
                    <div className="error">{errors[input.id]}</div>
                  )}
                </div>
              ))}

              {/* Corporation Fields Section */}
              {data.trust === 'corporation' && (
                <div className="corporation-section">
                  <div className="section-title">Corporation Information</div>

                  <div className="formInput">
                    <label htmlFor="corpName">Name of Corporation</label>
                    <input
                      id="corpName"
                      type="text"
                      placeholder="e.g. Kwahu Praso Rural Bank Ltd"
                      onChange={handleInput}
                      value={data.corpName || ''}
                      required
                    />
                    {errors.corpName && (
                      <div className="error">{errors.corpName}</div>
                    )}
                  </div>

                  <div className="formInput">
                    <label htmlFor="doi">Date of Incorporation</label>
                    <input
                      id="doi"
                      type="date"
                      onChange={handleInput}
                      value={data.doi || ''}
                      required
                    />
                    {errors.doi && <div className="error">{errors.doi}</div>}
                  </div>

                  <div className="formInput">
                    <label htmlFor="tax">Tax Identification Number</label>
                    <input
                      id="tax"
                      type="text"
                      placeholder="GH-00-0000"
                      onChange={handleInput}
                      value={data.tax || ''}
                      required
                    />
                    {errors.tax && <div className="error">{errors.tax}</div>}
                  </div>

                  <div className="formInput">
                    <label htmlFor="certificate">
                      Certificate of Incorporation
                    </label>
                    <input
                      type="file"
                      id="certificate"
                      onChange={handleFileChange}
                      accept="image/*,application/pdf"
                      required
                    />
                    {errors.certificate && (
                      <div className="error">{errors.certificate}</div>
                    )}
                  </div>
                </div>
              )}

              {errors.general && <div className="error">{errors.general}</div>}

              <button
                disabled={isReadOnlyRole || (per !== null && per < 100)}
                type="submit"
                className={isReadOnlyRole ? 'disabled' : ''}
              >
                {per !== null && per < 100
                  ? `Uploading... ${Math.round(per)}%`
                  : 'Create Shareholder'}
              </button>
            </ReadOnlyForm>
          </div>
        </div>
      </div>
    </div>
  );
};

export default New;
